import { useEffect, useMemo, useState } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiClient from 'api/apiClients';
import type { ClientPayload } from 'api/apiClients';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import ClientContacts, { type ClientContactRow } from '../ClientContacts';
import ClientEditForm from './ClientEditForm';

interface OffClientEditProps {
    clientId: number | null;
    setClientId: (clientId: number | null) => void;
    fetchClients: (force?: boolean) => Promise<void>;
    onCreated?: (clientId: number) => void | Promise<void>;
}

const emptyClient: ClientPayload = {
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    clientLocation: '',
    clientDescription: '',
    clientStatus: 0,
};

const OffClientEdit = ({
    clientId,
    setClientId,
    fetchClients,
    onCreated,
}: OffClientEditProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showToast } = useToast();
    const [client, setClient] = useState<ClientPayload>(emptyClient);
    const [draftContacts, setDraftContacts] = useState<ClientContactRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingClient, setLoadingClient] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const isCreate = clientId === 0;

    const title = useMemo(
        () => (isCreate ? t('edit.titleCreate') : t('edit.titleUpdate')),
        [isCreate, t],
    );

    const handleClose = () => {
        setClientId(null);
        setClient(emptyClient);
        setDraftContacts([]);
        setActiveTab('general');
    };

    const fetchClient = async () => {
        if (!clientId || isCreate) return;

        setLoadingClient(true);
        const resClient = await apiClient.getClientById(clientId);
        setLoadingClient(false);

        if (!resClient) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        setClient({
            clientName: resClient.clientName || '',
            clientPhone: resClient.clientPhone || '',
            clientEmail: resClient.clientEmail || '',
            clientAddress: resClient.clientAddress || '',
            clientLocation: resClient.clientLocation || '',
            clientDescription: resClient.clientDescription || '',
            clientStatus: resClient.clientStatus ?? 0,
        });
    };

    const handleSubmit = async (payload: ClientPayload) => {
        setLoading(true);

        try {
            if (isCreate) {
                const result = await apiClient.createClient({
                    ...payload,
                    contacts: draftContacts.map(({ contactType, contactValue }) => ({
                        contactType,
                        contactValue,
                    })),
                });
                if (result.ok === false) {
                    if (result.errCode === 'CLIENT_EMAIL_EXISTS') {
                        showToast(t('messClientEmailExists'), 'danger');
                        return;
                    }
                    showToast(t('common:messError'), 'danger');
                    return;
                }

                await onCreated?.(result.clientId);
            } else {
                const stat = await apiClient.updateClient(clientId as number, payload);
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
            }

            showToast(t(isCreate ? 'messClientCreated' : 'messClientUpdated'), 'success');
            await fetchClients(true);
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId === null) return;
        setActiveTab('general');
        if (isCreate) {
            setClient(emptyClient);
            setDraftContacts([]);
            return;
        }
        fetchClient();
    }, [clientId]);

    return (
        <Offcanvas show={clientId !== null} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {loadingClient ? (
                    <ESpinner />
                ) : (
                    <div className="off-client-tabs d-flex flex-column h-100">
                        <Tabs
                            variant="underline"
                            activeKey={activeTab}
                            onSelect={(tab) => tab && setActiveTab(tab)}
                            className="mb-0 invoice-tab border-bottom px-3"
                        >
                            <Tab
                                eventKey="general"
                                title={
                                    <>
                                        <SolarIcon name="info-circle" className="me-2 fs-14" />
                                        {t('tabGeneral')}
                                    </>
                                }
                            >
                                <div className="d-flex flex-column h-100 pt-3">
                                    <ClientEditForm
                                        initialValues={client}
                                        loading={loading}
                                        submitLabel={isCreate ? t('toolAddClient') : t('common:buttons.save')}
                                        onSubmit={handleSubmit}
                                    />
                                </div>
                            </Tab>
                            <Tab
                                eventKey="contacts"
                                title={
                                    <>
                                        <SolarIcon name="person-vcard" className="me-2 fs-14" />
                                        {t('tabContacts')}
                                    </>
                                }
                            >
                                <div className="px-3 pt-3 pb-4 overflow-auto h-100">
                                    <ClientContacts
                                        clientId={isCreate ? 0 : clientId}
                                        mode="edit"
                                        drafts={draftContacts}
                                        onDraftsChange={setDraftContacts}
                                    />
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffClientEdit;
